import { isNullOrEmpty } from "common/strings";
import React, { useState, useEffect } from "react";
import ImportDetailsProps from "../importDetailsProps";
import { ImportDataType } from "../../partnerIntegrationService";
import { Button, Form } from "react-bootstrap";
import FilePicker from "common/ui/FilePicker";
import { CSVLink } from "react-csv";
import Papa from "papaparse";
import notificationService from "common/notificationService";

const emptyRow: RecordAndLocation = { record: "", location: "" }


interface RecordAndLocationCustomImportDetailsProps extends ImportDetailsProps {
    enableSettlementImport: boolean;
}

const RecordAndLocationCustomImportDetails: React.FC<RecordAndLocationCustomImportDetailsProps> = props => {
    
    const [importArgs, setImportArgs] = useState<RecordAndLocationCustomImportArgs>(null);
    const [isFileInput, setIsFileInput] = useState(false);
    const [file, setFile] = useState<File>(null);

    const csvData = [
        ["RecordNumber", "Location"],
        ["Record 1", "Location 1"],
    ];

    const dataTypes = [
        { name: "Contracts", type: ImportDataType.Contract, enabled: true },
        { name: "Settlements", type: ImportDataType.Settlement, enabled: props.enableSettlementImport }
    ];

    useEffect(() => {
        props.onIsValidChanged(isValid());
    }, [props.request, isFileInput, file]);

    useEffect(() => {
        setImportArgs({ recordsAndLocations: [{...emptyRow}] });
        props.onChange({...props.request, dataTypes: [dataTypes.find(dt => dt.enabled).type]});
    }, []);


    function toggleRecordType(type: ImportDataType, enabled: boolean) {
        const newDataTypes = enabled
            ? [type]
            : [];
        props.onChange({...props.request, dataTypes: newDataTypes});
    }
    
    function isValid(): boolean {
        if (props.request.dataTypes.length !== 1) return false;

        return validateRecordsAndLocations(importArgs?.recordsAndLocations) && importArgs.recordsAndLocations.length <= 1500;
    }

    function validateRecordsAndLocations(records: RecordAndLocation[]): boolean {
        return records?.length > 0 &&
        records.every(m => !isNullOrEmpty(m.record) && !isNullOrEmpty(m.location));
    }

    function onUpdate(newRecord: RecordAndLocation, index: number) {
        const rows = [...importArgs.recordsAndLocations];
        rows.splice(index, 1, newRecord);
        const newArgs = {...importArgs, recordsAndLocations: rows}
        setImportArgs(newArgs)
        props.onChange({...props.request, customArguments: newArgs})
    }

    function resetArgs() {
        const newArgs = {...importArgs, recordsAndLocations: [{...emptyRow}]}
        setImportArgs(newArgs);
        setFile(null);
        props.onChange({...props.request, customArguments: newArgs})
    }

    function onFilePicked(file: File, setFile: React.Dispatch<React.SetStateAction<File>>) {
        Papa.parse(file, {
            header: true,   
            skipEmptyLines: true,
            complete: function (results) {
                const data: RecordAndLocation[] = results.data.map((row: any) => ({
                    record: row?.RecordNumber,
                    location: row?.Location,
                } as unknown as RecordAndLocation));
                const newArgs = {...importArgs, recordsAndLocations: data}
                let isValid = validateRecordsAndLocations(newArgs.recordsAndLocations);
                if(!isValid){
                    setFile(null);
                    notificationService.error("Error parsing file. Please check the file and try again.");
                    return;
                }
                setImportArgs(newArgs)
                props.onChange({...props.request, customArguments: newArgs})
            },
        });
        return setFile(file);
    }

    if (importArgs == null) return null;

    return <div className="erp-custom-import">
        <p>System will retrieve the specified records from ERP.
            <br />
            <strong>Note:</strong> ERP requires the record location to be passed in order to do a look-up
        </p>
        <div className="mb-2 row">
            <div className="col-3">
                <strong>Record Type:</strong>
                <div className="pl-4 row">
                    {dataTypes.filter(dt => dt.enabled).map((dt, i) => <Form.Check
                        key={i}
                        type="radio"
                        name="specificRecordsType"
                        id={"specificRecordsType" + i}
                        label={dt.name}
                        checked={props.request.dataTypes.includes(dt.type)}
                        onChange={ev => toggleRecordType(dt.type, ev.target.checked)}
                    />)}                    
                </div>
            </div>
            <div className="col-3">
                <strong>Input type:</strong>
                <div className="pl-4 row">
                    <Form.Check
                        type="radio"
                        label="Manual Entry"
                        name="inputMethodManual"
                        id="inputMethodManual"
                        checked={!isFileInput}
                        onChange={() => {
                            setIsFileInput(false);
                            resetArgs();
                        }}
                    />
                    <Form.Check
                        type="radio"
                        label="Upload CSV File"
                        name="inputMethodFile"
                        id="inputMethodFile"
                        checked={isFileInput}
                        onChange={() => {
                            setIsFileInput(true);
                            resetArgs();
                        }}
                    />                
                </div>
            </div>
        </div>

        {!isFileInput ? (
            <>
                {importArgs.recordsAndLocations.map((m, i) =>
                    <RecordAndLocationRow key={i}
                        data={m}
                        onUpdate={m => onUpdate(m, i)} />
                )}

                <button className="btn btn-primary mt-2"
                    onClick={() => setImportArgs({ ...importArgs, recordsAndLocations: [...importArgs.recordsAndLocations, { ...emptyRow }] })}>
                    Add Another
                </button>
            </>
        ) : (
            <>
                <div className="alert alert-info mt-2">
                    <strong>Note:</strong> Please select a csv file with the following columns (max records 1500):
                    <ul>
                        <li>Record Number</li>
                        <li>Location ID</li>
                    </ul>
                    <CSVLink className="alert-secondary pl-1 pr-1" data={csvData} filename={"ErpRecordsImportTemplate.csv"}>
                                <i className="fa fa-download info-circle"></i>
                                <span> DOWNLOAD BLANK TEMPLATE</span></CSVLink>
                </div>
                <div className="d-flex">
                    <input
                        name="file-name"
                        type="text"
                        value={file?.name || ''}
                        readOnly
                        className="col-4 form-control"
                    />
                    {
                        file == null ?
                            <FilePicker
                                onFilePicked={(files: File[]) => onFilePicked(files[0], setFile)}
                                acceptTypes=".csv"
                                maxFiles={1} /> :
                            <Button className="text-decoration-none d-flex align-items-center" variant="link"
                                onClick={() => setFile(null)}>
                                <i className="material-icons">delete</i> Delete File
                            </Button>
                    }
                </div>
            </>
        )
        }
    </div>
}

interface RecordAndLocationRowProps {
    data: RecordAndLocation;
    onUpdate(data: RecordAndLocation): void;
}

const RecordAndLocationRow: React.FC<RecordAndLocationRowProps> = props => {
    return <div className="row">
        <div className="col-3">
            <input type="text" className="form-control"
                placeholder="Record Number"
                value={props.data.record}
                onChange={ev => props.onUpdate({...props.data, record: ev.target.value})}
                />
        </div>
        
        <div className="col-3">
            <input type="text" className="form-control"
                placeholder="Location ID"
                value={props.data.location}
                onChange={ev => props.onUpdate({...props.data, location: ev.target.value})}
                />
        </div>
    </div>
}

interface RecordAndLocationCustomImportArgs {
    recordsAndLocations: RecordAndLocation[];
}

interface RecordAndLocation {
    record: string;
    location: string;
}

export default RecordAndLocationCustomImportDetails;